import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Paper, Typography, Button, Chip, CircularProgress } from '@mui/material';
import AISearchInput from './components/AISearchInput';
// import AISuggestions from './components/AISuggestions';
import { AITypingState, AISearchResult, AISearchRequest } from './types';
import { cleanQuery, matchesQuery } from './helpers';
import { searchShipmentHistoryAI } from './smartSearchService';
import { DEFAULT_SUGGESTIONS } from './constants';
import SentimentDissatisfiedIcon from '@mui/icons-material/SentimentDissatisfied';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import moment from 'moment';

interface AISmartSearchProps {
  onResults: (filters: AISearchResult['filters'] | null, activeResult: AISearchResult | null, error?: any) => void;
  isOpen?: boolean;
  onClose?: () => void;
  isGlowActive?: boolean;
  enumOptions: AISearchRequest['enumOptions'];
  onSearchStart?: () => void;
  totalRecords?: number;
  isListLoading?: boolean;
  type?: string;
  defaultSuggestions?: string[];
  emptyStateTitle?: string;
  hideCloseButton?: boolean;
}

export default function AISmartSearch({
  onResults,
  isOpen = true,
  onClose = () => undefined,
  isGlowActive: _isGlowActive = false,
  enumOptions,
  onSearchStart,
  totalRecords = 0,
  isListLoading = false,
  type = 'shipmentList',
  defaultSuggestions = DEFAULT_SUGGESTIONS,
  emptyStateTitle = "I couldn't find shipments matching:",
  hideCloseButton = true,
}: AISmartSearchProps) {
  const [query, setQuery] = useState('');
  const [typingState, setTypingState] = useState<AITypingState>('idle');
  const [result, setResult] = useState<AISearchResult | null>(null);
  // const [suggestions, setSuggestions] = useState<string[]>([]);

  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastExecutedQueryRef = useRef('');
  const requestIdRef = useRef(0);

  // Load suggested searches from local constants/storage whenever the panel is opened
  useEffect(() => {
    // if (isOpen) {
    //   setSuggestions(defaultSuggestions);
    // }

    // Always reset input and temporary states whenever the panel is toggled (opened or closed)
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    setQuery('');
    setTypingState('idle');
    setResult(null);
    lastExecutedQueryRef.current = '';
  }, [isOpen, defaultSuggestions]);

  // Primary search executor
  const executeSearch = useCallback(
    (q: string) => {
      const cleaned = cleanQuery(q);

      // Cancel any pending timers
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Ignore queries shorter than 3 chars or whitespace-only
      if (!cleaned || cleaned.length < 3) {
        setTypingState('idle');
        setResult(null);
        onResults(null, null);
        lastExecutedQueryRef.current = '';
        return;
      }

      // Prevent duplicate API calls if query is exactly the same as last executed
      if (matchesQuery(cleaned, lastExecutedQueryRef.current)) {
        return;
      }

      // Setup race condition protection
      const reqId = ++requestIdRef.current;
      setTypingState('thinking');
      lastExecutedQueryRef.current = cleaned;

      // Start loading animation in parent
      if (onSearchStart) {
        onSearchStart();
      }

      searchShipmentHistoryAI({
        type,
        text: cleaned,
        enumOptions,
      })
        .then(res => {
          // Ignore stale responses
          if (reqId !== requestIdRef.current) return;

          // Check if interpret backend returned status 500 error payload
          if (res && res.status === 500) {
            setTypingState('error');
            onResults(null, null, res);
            return;
          }

          setResult(res);
          setTypingState('success');

          // Propagate results upwards
          onResults(res.filters, res);
        })
        .catch(err => {
          if (reqId !== requestIdRef.current) return;
          console.error('AI search failed', err);
          setTypingState('error');
          onResults(null, null, err);
        });
    },
    [onResults, enumOptions, onSearchStart, type],
  );

  // Handle change in search input
  const handleQueryChange = (val: string) => {
    setQuery(val);

    const cleaned = cleanQuery(val);
    if (!cleaned) {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      setTypingState('idle');
      setResult(null);
      onResults(null, null);
      lastExecutedQueryRef.current = '';
      return;
    }

    setTypingState('typing');

    // Debounce: Cancel previous timer and start a new 1000ms timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      setTypingState('debouncing');
      executeSearch(val);
    }, 1000);
  };

  // Skip debounce and execute immediately
  const handleForceSearch = () => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    executeSearch(query);
  };

  // Reset/Clear search
  const handleClear = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    setQuery('');
    setTypingState('idle');
    setResult(null);
    lastExecutedQueryRef.current = '';

    onResults(null, null);
  }, [onResults]);

  // Refine query (conversational memory)

  // const handleSelectSuggestion = (val: string) => {
  //   setQuery(val);
  //   executeSearch(val);
  // };

  // Helper to map and render active filters
  const renderAppliedFilters = () => {
    if (!result || !result.filters) return null;
    const filters = result.filters;

    const chips: { label: string }[] = [];

    // 1. Date Range
    if (filters.fromDate && filters.toDate) {
      const start = moment(filters.fromDate, ['YYYY-MM-DD', 'DD/MM/YYYY']);
      const end = moment(filters.toDate, ['YYYY-MM-DD', 'DD/MM/YYYY']);
      const label =
        start.isValid() && end.isValid()
          ? `Date: ${start.format('MMM D, YYYY')} → ${end.format('MMM D, YYYY')}`
          : `Date: ${filters.fromDate} → ${filters.toDate}`;
      chips.push({ label });
    } else if (filters.fromDate) {
      const start = moment(filters.fromDate, ['YYYY-MM-DD', 'DD/MM/YYYY']);
      const label = start.isValid() ? `Date From: ${start.format('MMM D, YYYY')}` : `Date From: ${filters.fromDate}`;
      chips.push({ label });
    } else if (filters.toDate) {
      const end = moment(filters.toDate, ['YYYY-MM-DD', 'DD/MM/YYYY']);
      const label = end.isValid() ? `Date To: ${end.format('MMM D, YYYY')}` : `Date To: ${filters.toDate}`;
      chips.push({ label });
    }

    // 2. Carrier
    if (filters.carrier && filters.carrier.length > 0 && enumOptions.carrier) {
      filters.carrier.forEach((id: string) => {
        const matched = enumOptions.carrier!.find(c => c.value === id);
        chips.push({ label: `Carrier: ${matched ? matched.label : id}` });
      });
    }

    // 3. Service
    if (filters.service && filters.service.length > 0 && enumOptions.service) {
      filters.service.forEach((id: string) => {
        const matched = enumOptions.service!.find(s => s.value === id);
        chips.push({ label: `Service: ${matched ? matched.label : id}` });
      });
    }

    // 4. Systems
    if (filters.systems && filters.systems.length > 0 && enumOptions.systems) {
      filters.systems.forEach((id: string) => {
        const matched = enumOptions.systems!.find(sys => sys.value === id);
        chips.push({ label: `System: ${matched ? matched.label : id}` });
      });
    }

    // 5. Status / onBoardedStatus
    const statusValues = filters.onBoardedStatus || filters.status;
    if (statusValues && statusValues.length > 0) {
      statusValues.forEach((val: string) => {
        let label = val.charAt(0).toUpperCase() + val.slice(1).replace(/_/g, ' ');
        if (enumOptions.status) {
          const matched = enumOptions.status.find((s: any) => s.value === val);
          if (matched) label = matched.label || matched.display || label;
        }
        chips.push({ label: `Status: ${label}` });
      });
    }

    // 6. Account Type
    if (filters.accountType && filters.accountType.length > 0) {
      filters.accountType.forEach((val: string) => {
        let label = val.charAt(0).toUpperCase() + val.slice(1).replace(/_/g, ' ');
        if (enumOptions.accountType) {
          const matched = enumOptions.accountType.find((a: any) => a.value === val);
          if (matched) label = matched.label || matched.display || label;
        }
        chips.push({ label: `Account Type: ${label}` });
      });
    }

    // KYC Status
    if (filters.kyc && filters.kyc.length > 0) {
      filters.kyc.forEach((val: string) => {
        let label = val.charAt(0).toUpperCase() + val.slice(1).replace(/_/g, ' ');
        if (enumOptions.kyc) {
          const matched = enumOptions.kyc.find((k: any) => k.value === val);
          if (matched) label = matched.label || matched.display || label;
        }
        chips.push({ label: `KYC: ${label}` });
      });
    }
    // Access Group
    if (filters.accessGroup && filters.accessGroup.length > 0) {
      filters.accessGroup.forEach((id: string) => {
        let label = id;
        if (enumOptions.accessGroup) {
          const matched = enumOptions.accessGroup.find((a: any) => a.value === id);
          if (matched) label = matched.label || matched.display || label;
        }
        chips.push({ label: `Access Group: ${label}` });
      });
    }

    // 7. Shipment Type
    if (filters.type && filters.type.length > 0) {
      filters.type.forEach((val: string) => {
        const label = val.charAt(0).toUpperCase() + val.slice(1).replace(/_/g, ' ');
        chips.push({ label: `Type: ${label}` });
      });
    }

    // 7. Route Type
    if (filters.routeType) {
      const label = String(filters.routeType).charAt(0).toUpperCase() + String(filters.routeType).slice(1);
      chips.push({ label: `Route: ${label}` });
    }

    // 8. Packaging
    if (filters.packaging != null) {
      chips.push({ label: `Packaging: ${filters.packaging}` });
    }

    // 9. Insurance
    if (filters.insurance != null) {
      chips.push({ label: `Insurance: ${filters.insurance}` });
    }

    // 10. Invoice Type / Invoice Status
    const invTypeValues = filters.invoice_type || filters.invoiceType;
    if (invTypeValues && invTypeValues.length > 0) {
      invTypeValues.forEach((val: string) => {
        let label = val.charAt(0).toUpperCase() + val.slice(1).replace(/_/g, ' ');
        if (enumOptions.invoice_type) {
          const matched = enumOptions.invoice_type.find((it: any) => it.value === val);
          if (matched) label = matched.label || matched.display || label;
        }
        chips.push({ label: `Invoice Type: ${label}` });
      });
    } else if (filters.invoiceStatus) {
      const statuses = Array.isArray(filters.invoiceStatus) ? filters.invoiceStatus : [filters.invoiceStatus];
      statuses.forEach((val: string) => {
        const label = val.charAt(0).toUpperCase() + val.slice(1).replace(/_/g, ' ');
        chips.push({ label: `Invoice: ${label}` });
      });
    }

    // 11. Search Key / Value
    if (filters.searchKey && filters.searchValue) {
      chips.push({ label: `${filters.searchKey}: ${filters.searchValue}` });
    }

    if (chips.length === 0) return null;

    return (
      <Box sx={{ mt: 2, mb: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.light', fontSize: '13px' }}>
          Applied AI Filters
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {chips.map((c, i) => (
            <Chip
              key={i}
              label={c.label}
              size="small"
              sx={{
                backgroundColor: 'background.paper',
                border: '1px solid',
                borderColor: 'primary.light',
                color: 'primary.main',
                fontWeight: 600,
                borderRadius: '16px',
                '& .MuiChip-label': { px: 1.5, py: 0.5, fontSize: '12px' },
              }}
            />
          ))}
        </Box>
      </Box>
    );
  };

  // const hasNoInput = !query.trim();

  // If the panel is closed, render nothing but keep component mounted to preserve search state
  if (!isOpen) {
    return null;
  }

  return (
    <Box sx={{ width: '100%', my: 3 }}>
      <Paper
        variant="outlined"
        sx={{
          p: 4,
          borderRadius: '24px',
          border: '1px solid rgba(0, 0, 0, 0.08)',
          backgroundColor: '#ffffff',
          boxSizing: 'border-box',
          position: 'relative',
          transition: 'all 0.3s ease',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
        }}
      >
        {/* Header section with Close button */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AutoAwesomeIcon sx={{ color: 'primary.main', fontSize: '20px' }} />
            <Typography variant="h6" sx={{ fontWeight: 800, color: 'text.primary', fontSize: '18px' }}>
              AI Smart Search
            </Typography>
          </Box>
          {!hideCloseButton && (
            <Button
              size="small"
              variant="text"
              onClick={onClose}
              sx={{
                color: '#64748b',
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '13px',
                px: 2,
                py: 0.5,
                borderRadius: '20px',
                backgroundColor: '#f1f5f9',
                '&:hover': {
                  color: '#334155',
                  backgroundColor: '#e2e8f0',
                },
              }}
            >
              Minimize Workspace
            </Button>
          )}
        </Box>

        {/* Input container */}
        <Box sx={{ position: 'relative', width: '100%', mb: 1 }}>
          <AISearchInput
            value={query}
            typingState={typingState}
            onChange={handleQueryChange}
            onClear={handleClear}
            onForceSearch={handleForceSearch}
            autoFocus={isOpen}
          />
        </Box>

        {/* Applied AI Filters summary */}
        {typingState === 'success' && renderAppliedFilters()}

        {/* Thinking state spinner */}
        {typingState === 'thinking' && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress size={36} sx={{ color: 'primary.main' }} />
          </Box>
        )}

        {/* Suggested queries (rendered when input is empty) */}
        {/* {hasNoInput && <AISuggestions onSelect={handleSelectSuggestion} suggestions={suggestions} />} */}

        {/* Smart Empty State */}
        {typingState === 'success' && !isListLoading && totalRecords === 0 && (
          <Paper
            variant="outlined"
            sx={{
              p: 4,
              borderRadius: '16px',
              borderColor: '#e2e8f0',
              backgroundColor: '#f8fafc',
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              mt: 2,
            }}
          >
            <SentimentDissatisfiedIcon sx={{ fontSize: 48, color: '#94a3b8' }} />
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#334155', mb: 0.5 }}>
                {emptyStateTitle}
              </Typography>
              <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#64748b', fontWeight: 600 }}>
                "{query || result?.query || ''}"
              </Typography>
            </Box>
          </Paper>
        )}

        {/* Error state */}
        {typingState === 'error' && (
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              borderRadius: '16px',
              borderColor: '#fca5a5',
              backgroundColor: '#fef2f2',
              textAlign: 'center',
            }}
          >
            <Typography variant="subtitle1" sx={{ color: '#dc2626', fontWeight: 700, mb: 1 }}>
              Something went wrong while processing your request
            </Typography>
            <Button variant="outlined" color="error" size="small" onClick={handleForceSearch}>
              Try Again
            </Button>
          </Paper>
        )}
      </Paper>
    </Box>
  );
}
